import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild, ViewEncapsulation } from '@angular/core';
import { isPlatformServer } from '@angular/common';

import { FormEventService, FormFeatureService, FormSubmitService, viewProviders } from './view-providers';
import { SearchLocationData, SearchLocationService } from '@hiptraveler/common';
import { PlanTripService } from '../plan-trip.service';

// Legacy functions
import { isWidget } from '@hiptraveler/common';
import { ExperienceFinderStateService } from '@hiptraveler/features/experience-finder';
import { getFiltersService } from '../../search-filter-ref';
import { Store } from '@ngxs/store';
import { delay, filter, firstValueFrom, map, skip, takeUntil, tap } from 'rxjs';
import * as moment from 'moment';

@Component({
  selector: 'plan-trip-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  host: { class: 'experience-finder-screen-plan-trip-form' },
  encapsulation: ViewEncapsulation.None,
  viewProviders
})
export class FormComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('placeAutocomplete') placeAutocomplete: ElementRef<HTMLInputElement>;

  minDate = new Date().toISOString();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    public formFeature: FormFeatureService,
    public formSubmit: FormSubmitService,
    public formEvent: FormEventService,
    private planTrip: PlanTripService,
    private searchLocation: SearchLocationService,
    
    // Legacy functions
    private store: Store,
    private stateService: ExperienceFinderStateService
  ) { }

  async ngOnInit(): Promise<void> {

    this.planTrip.externalViewState$$.next(false);
    this.searchLocation.data && this.locationChanged(this.searchLocation.data);
    this.searchLocation.searchLocation$.subscribe(value => this.locationChanged(value));
    this.legacyFunctions();
  }

  ngOnDestroy(): void {
    this.planTrip.externalViewState$$.next(false);
    if (!this.searchLocation.data?.locId) {
      sessionStorage.setItem('_ht_exp_finder_location_store', JSON.stringify(this.formFeature.placeResult));
    } else {
      sessionStorage.removeItem('_ht_exp_finder_location_store');
    }
  }

  ngAfterViewInit(): void {

    if (isPlatformServer(this.platformId)) return;

    this.formEvent.initialize({ placeAutocomplete: this.placeAutocomplete.nativeElement });
    this.formFeature.syncDaysAndDateRangeInputFields();
  }

  locationChanged(value: SearchLocationData): void {
    const { locId: place_id, location: formatted_address, name, latitude, longitude } = value;
    const location: any = {
      lat: () => +(latitude || '0'),
      lng: () => +(longitude || '0')
    };
    this.formFeature.form.patchValue({ place: name });
    setTimeout(() => {
      this.placeAutocomplete.nativeElement.value = name || '';
      this.formFeature.placeResult = { place_id, formatted_address, name, geometry: { location }, types: [value?.type || ''] };
      sessionStorage.removeItem('_ht_exp_finder_location_store');
    }, 10);
  }
  
  submit(): void {
    sessionStorage.removeItem('_ht_exp_finder_location_store');
    this.formSubmit.submit({ placeResult: this.formFeature.placeResult });
  }

  // Legacy functions
  private async legacyFunctions(): Promise<void> {

    this.stateService.processing$$.pipe(
      map(isProcessing => isProcessing ? 'none' : 'auto'),
      tap(pointer => {
        const inputFieldEl = document.querySelector('explore-input-field') as HTMLElement;
        const filterContainerEl = document.querySelector('.search-page-root--filters') as HTMLElement;
        inputFieldEl?.style && (inputFieldEl.style.pointerEvents = pointer);
        filterContainerEl?.style && (filterContainerEl.style.pointerEvents = pointer);
      }),
    ).subscribe();

    if (isWidget()) return;

    if (!this.searchLocation.data?.locId) {
      const locationStore = JSON.parse(sessionStorage.getItem('_ht_exp_finder_location_store') || '{}');
      this.formFeature.placeResult = locationStore;
      this.formFeature.form.patchValue({ place: locationStore?.name || '' });
      setTimeout(() => {
        this.placeAutocomplete.nativeElement.value = locationStore?.name || '';
      }, 10);
    }

    await firstValueFrom(this.stateService.overlayState$.pipe(filter(Boolean)));

    // --- ON INIT: updates expfinder from the url query params

    const { checkInDate, checkOutDate } = Object.fromEntries(
      new URLSearchParams(window.location.search)
    );
    this.formFeature.form.patchValue({
      startDate: checkInDate ? moment(checkInDate) : undefined,
      endDate: checkOutDate ? moment(checkOutDate) : undefined,
    });

    // --- DATES: expfinder updates the search filters

    const filters = getFiltersService();
    let dont_update_date = false;

    this.formFeature.form.valueChanges.pipe(
      skip(1),
      takeUntil( this.stateService.overlayState$.pipe(map(e => !e), skip(1)) )
    ).subscribe(({ startDate, endDate }) => {
      if (dont_update_date) return;

      const _startDate = (startDate as moment.Moment)?.unix();
      const _endDate = (startDate as moment.Moment)?.unix();
      if (!Number.isNaN(_startDate) && !Number.isNaN(_endDate)) {
        filters?.form?.patchValue({ checkInDate: startDate, checkOutDate: endDate });
      }
    });
    
    // --- DATES: search filters updates the expfinder

    filters?.form?.get('checkOutDate')?.valueChanges?.pipe(delay(200)).subscribe(() => {
      if (this.stateService.dialogView$$.value !== 'plan-trip') return;
      const { checkInDate: startDate, checkOutDate: endDate } = filters.form.value;
      if (!endDate) return;
      dont_update_date = true;
      this.formFeature.form.patchValue({ startDate, endDate });
      setTimeout(() => (dont_update_date = true), 10);
    });
  }

}
